export enum IncentiveOfferType {
  STORE_CREDIT = 'STORE_CREDIT',
  DISCOUNT_CODE = 'DISCOUNT_CODE',
}

export enum FeeType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED = 'FIXED',
}

export enum FeeAppliesTo {
  REFUND = 'REFUND',
  STORE_CREDIT = 'STORE_CREDIT',
  BOTH = 'BOTH',
}

export enum FeeChargeBasis {
  PER_RETURN = 'PER_RETURN',
  PER_MATCHING_UNIT = 'PER_MATCHING_UNIT',
}

export enum FeeEligibilityScope {
  ALL_RETURNS = 'ALL_RETURNS',
  MATCHING_ITEMS = 'MATCHING_ITEMS',
}

export enum FeeConditionField {
  SKU = 'SKU',
}

export enum FeeConditionOperator {
  EQUALS = 'EQUALS',
  STARTS_WITH = 'STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
  CONTAINS = 'CONTAINS',
  IN = 'IN',
}

export interface FeeEligibilityCondition {
  id: string;
  field: FeeConditionField;
  operator: FeeConditionOperator;
  value: string;
}

export interface FeeEligibility {
  scope: FeeEligibilityScope;
  conditions: FeeEligibilityCondition[];
}

export interface DynamicFee {
  id: string;
  name: string;
  amount: number;
  feeType: FeeType;
  appliesTo?: FeeAppliesTo;
  enabled?: boolean;
  chargeBasis?: FeeChargeBasis;
  eligibility?: FeeEligibility;
}

export enum IncentiveValueType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
}

export enum IncentiveFlowStage {
  RETURN_DEFLECTION = 'returnDeflection',
  REFUND_OPTIMIZATION = 'refundOptimization',
}

export interface IncentiveOption {
  id: string;
  offerType: IncentiveOfferType;
  value: number;
  valueType: IncentiveValueType;
  label?: string; // Optional label e.g., "Get $20 Store Credit"
}

export type IncentiveItemScope = 'ALL_ITEMS' | 'MATCHING_ITEMS';

export interface IncentiveCatalogReference {
  id: string;
  title: string;
}

export interface IncentiveItemSelectors {
  products: IncentiveCatalogReference[];
  skus: string[];
  collections: IncentiveCatalogReference[];
  tags: string[];
}

export interface IncentiveItemEligibility {
  scope: IncentiveItemScope;
  include: IncentiveItemSelectors;
  exclude: IncentiveItemSelectors;
}

export interface IncentiveRule {
  id: string;
  name: string;
  minItemValue: number;
  maxItemValue?: number | null;
  requiresProtection: boolean;
  options: IncentiveOption[];
  isEnabled: boolean;
  eligibility?: IncentiveItemEligibility;
}

export interface RefundOptimizationRule {
  id: string;
  name: string;
  minItemValue: number;
  maxItemValue?: number | null;
  requiresProtection: boolean;
  bonusValue: number; // % Bonus
  isEnabled: boolean;
}

export enum ShippingCostResponsibility {
  MERCHANT_PAYS = 'MERCHANT_PAYS',
  CUSTOMER_PAYS_FULL = 'CUSTOMER_PAYS_FULL',
  CUSTOMER_PAYS_FLAT_FEE = 'CUSTOMER_PAYS_FLAT_FEE',
}

export interface ShippingCostConfig {
  responsibility: ShippingCostResponsibility;
  flatFeeAmount?: number;
  flatFeeAmountsByCurrency?: Record<string, number>;
}

export interface RefundFallback {
  allowAdditionalFees?: boolean;
  waiveFeesForProtectedOrders?: boolean;
  dynamicFees?: DynamicFee[];
  shippingCost?: {
    protectedOrders: ShippingCostConfig;
    unprotectedOrders: ShippingCostConfig;
  };
}

export interface IncentiveSettings {
  flowOrder?: IncentiveFlowStage[];
  returnDeflection?: {
    enabled?: boolean;
    // Legacy support (optional, for backward compat during migration)
    keepItemIncentive?: {
      value?: number;
      offerType?: IncentiveOfferType;
    };
    rules?: IncentiveRule[];
  };
  refundOptimization?: {
    enabled?: boolean;
    storeCreditBonus?: number;
    rules?: RefundOptimizationRule[];
  };
  bounds?: {
    smartEligibilityCheck?: boolean;
  };
  refundFallback?: RefundFallback;
}
